// SIM-1502 — tenant picker for the Dottie context editor. Selection lives in URL state
// (`?tenantId=`) so deep links survive reloads, and the picker stays presentational. SIM-1611: each
// tenant carries its resolved `ai_agent.dottie` flag state — enabled tenants float to the top and
// disabled ones stay selectable but dimmed, so nobody authors context for a tenant where Dottie is
// off. Only `useTenants()` blocks rendering; the flag overlay is non-blocking and degrades to the
// unenriched list rather than erroring (plus SIM-1680 C3's portal link).
import {
  Button,
  cn,
  QueryView,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@simplicity/ui";
import { useMemo } from "react";
import type { PortalTenant } from "./types";
import {
  type PortalTenantWithDottieState,
  useTenants,
  useTenantsWithDottieState,
} from "./useDottieContext";

interface TenantPickerProps {
  selectedTenantId: string | null;
  onChange: (tenantId: string) => void;
}

interface DisplayTenant {
  id: string;
  name: string;
  slug: string | null;
  dottieEnabled: boolean | null;
}

/**
 * Merge the base tenant list with the optional flag-state overlay. The base
 * list defines membership and the initial sort (alphabetical by name); when
 * the overlay is available, we re-sort enabled-first and attach slug + flag
 * state. The overlay is keyed by id so cross-list drift is impossible.
 */
function mergeTenants(
  base: PortalTenant[],
  overlay: PortalTenantWithDottieState[] | undefined,
): DisplayTenant[] {
  const overlayById = new Map(overlay?.map((t) => [t.id, t]) ?? []);

  const merged: DisplayTenant[] = base.map((t) => {
    const enriched = overlayById.get(t.id);
    return {
      id: t.id,
      name: t.name,
      slug: enriched?.slug ?? null,
      dottieEnabled: enriched ? enriched.dottieEnabled : null,
    };
  });

  if (!overlay) {
    // No overlay → preserve base alphabetical order.
    merged.sort((a, b) => a.name.localeCompare(b.name));
    return merged;
  }

  // Enabled tenants first, then alphabetical within each group. Tenants
  // that exist in the base list but not the overlay (rare race window
  // between the two queries) sort as if disabled.
  merged.sort((a, b) => {
    const aEnabled = a.dottieEnabled === true;
    const bEnabled = b.dottieEnabled === true;
    if (aEnabled !== bEnabled) return aEnabled ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return merged;
}

export function TenantPicker({ selectedTenantId, onChange }: TenantPickerProps) {
  const tenantsQuery = useTenants();
  const enrichedQuery = useTenantsWithDottieState();
  const tenants = useMemo(
    () => mergeTenants(tenantsQuery.data ?? [], enrichedQuery.data),
    [tenantsQuery.data, enrichedQuery.data],
  );

  return (
    <div className="space-y-2">
      <label htmlFor="dottie-tenant-select" className="text-sm font-medium text-foreground">
        Tenant
      </label>
      <QueryView
        query={tenantsQuery}
        error={(err) => (
          <p className="text-sm text-destructive" role="alert">
            Failed to load tenants: {err.message}
          </p>
        )}
      >
        {() => {
          if (tenants.length === 0) {
            return <p className="text-sm text-muted-foreground">No tenants available.</p>;
          }
          const value = selectedTenantId ?? tenants[0]?.id ?? "";
          const selectedTenant = tenants.find((t) => t.id === value) ?? null;
          return (
            <div className="space-y-2">
              <Select value={value} onValueChange={onChange}>
                <SelectTrigger id="dottie-tenant-select" className="w-full max-w-md">
                  <SelectValue placeholder="Select a tenant" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <TenantOptionLabel tenant={t} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTenant?.slug ? (
                <div className="flex max-w-md items-center justify-end">
                  <Button asChild variant="link" size="sm" className="h-auto px-0">
                    <a
                      href={`/portal/${selectedTenant.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open in portal
                    </a>
                  </Button>
                </div>
              ) : null}
            </div>
          );
        }}
      </QueryView>
    </div>
  );
}

function TenantOptionLabel({ tenant }: { tenant: DisplayTenant }) {
  const enabled = tenant.dottieEnabled;
  // When the overlay hasn't arrived yet (`null`), render just the name — no
  // indicator, no dim — so the picker doesn't flash empty / wrong state.
  if (enabled === null) {
    return <span>{tenant.name}</span>;
  }
  return (
    <span className={cn("flex items-center gap-2", !enabled && "text-muted-foreground")}>
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex h-4 w-4 shrink-0 items-center justify-center text-xs leading-none",
          enabled ? "text-success" : "text-muted-foreground",
        )}
      >
        {enabled ? "✓" : "○"}
      </span>
      <span>{tenant.name}</span>
      <span className="sr-only">{enabled ? "(Dottie enabled)" : "(Dottie off)"}</span>
    </span>
  );
}
